import React, { useState } from 'react';
import clsx from 'clsx';
import dayjs from 'dayjs';
import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import './exam-card.scss';
import { useSizes } from "@/utility/responsive.ts";
import TopicAnalysisModal from '@/components/student-exams/components/TopicAnalysisModal';

interface ExamProperty {
	displayedName: string;
	totalTrueAnswer: number;
	totalFalseAnswer: number;
	totalEmptyAnswer: number;
	totalUnseenAnswer: number;
}

export interface ExamCardData {
	examId: number;
	examName: string;
	itemCount: number;
	isDopingExam: boolean;
	endDate?: string;
	startDate?: string;
	totalNetAnswer?: string;
	point?: string;
	rank?: string;
	totalParticipant?: string;
	examProperties?: ExamProperty[];
}

interface ExamCardProps {
	data: ExamCardData;
	studentId: number;
}

const ExamCard: React.FC<ExamCardProps> = ({ data, studentId }) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [isTopicAnalysisOpen, setIsTopicAnalysisOpen] = useState(false);
    const { isMobile } = useSizes();

	return (
		<div className={clsx('exam-card', data.isDopingExam ? 'exam-card--doping' : 'exam-card--other', { 'exam-card--expanded': isExpanded })}>
			<div className="exam-card__header" onClick={() => setIsExpanded(prev => !prev)}>
				<div className="exam-card__info">
					<div className="exam-card__title">
						<span className="exam-card__name">{data.examName}</span>
						<span className="exam-card__question-count">{data.itemCount} Soru</span>
					</div>
					<div className="exam-card__date">
						<If condition={!!data.endDate || !!data.startDate}>
							{dayjs(data.endDate || data.startDate).format('DD.MM.YYYY')}
						</If>
					</div>
				</div>

				<div className="exam-card__extra">
						<Choose>
                            <When condition={isMobile}>
                                <div className="mobile-exam-card__score-group">
                                    <If condition={!data?.isDopingExam as boolean}>
                                        <div className="exam-card__score--other-exam">
                                            <div className="exam-card__score-title">
                                                <i className="icons-check-outline-circle" />
                                                <span className="label">Net</span>
                                            </div>
                                            <span className="exam-card__score-label"><strong>{data?.totalNetAnswer}</strong></span>
                                        </div>
                                    </If>
                                    <If condition={data?.isDopingExam && (data?.point || data?.rank)}>
                                        <div className="exam-card__score--doping-exam">
                                            <div className="exam-card__score--doping-exam-list">
                                                {
                                                    data?.point ?
                                                    <div className="mobile-exam-card__score">
                                                        <div className="mobile-exam-card__score-title">
                                                            <i className="icons-award" />
                                                            <span className="label">Puan</span>
                                                        </div>
                                                        <span className="mobile-exam-card__score-value">{data?.point}</span>
                                                    </div> : null
                                                }
                                                {
                                                    data?.rank ?
                                                    <div className="mobile-mobile-exam-card__score">
                                                        <div className="mobile-exam-card__score-title">
                                                            <i className="icons-users-2" />
                                                            <span className="label">Sıralama</span>
                                                        </div>
                                                        <span className="mobile-exam-card__score-value">{data?.rank}</span>
                                                    </div> : null
                                                }
                                            </div>
                                            {
                                                data?.totalParticipant ?
                                                <div className="exam-card__score--doping-exam-warning">
                                                 <i className="icons-info-circle" />   Sınava Giren Kişi Sayısı: <strong>{data?.totalParticipant}</strong>
                                                </div> : null
                                            }

                                        </div>
                                    </If>
                                </div>
                            </When>
                            <Otherwise>
                                <div className="exam-card__score-group">
                                    <If condition={(!data?.isDopingExam as boolean) && !!data?.totalNetAnswer}>
                                        <div className="exam-card__score">
                                            <i className="icons-check-outline-circle" />
                                            <span className="exam-card__score-label"><strong>{data?.totalNetAnswer}</strong> net</span>
                                        </div>
                                    </If>
                                    <If condition={data?.isDopingExam && !!data?.point}>
                                        <div className="exam-card__score">
                                            <i className="icons-award" />
                                            <span className="exam-card__score-label"><strong>{data?.point}</strong> puan</span>
                                        </div>
                                    </If>
                                    <If condition={data?.isDopingExam && !!data.rank}>
                                        <div className="exam-card__score">
                                            <i className="icons-users-2" />
                                            <span className="exam-card__score-label"><strong>{data.rank} / </strong>{data.totalParticipant}</span>
                                        </div>
                                    </If>
                                </div>
                            </Otherwise>
                        </Choose>

					<button
						type="button"
						className="exam-card__topic-btn"
						onClick={(e) => { e.stopPropagation(); setIsTopicAnalysisOpen(true); }}
					>
						Konu Bazlı Analiz
					</button>

					<button className="exam-card__toggle-btn">
						<i className={`icons-alt-arrow-${isExpanded ? 'up' : 'down'}`} />
					</button>
				</div>
			</div>

			<If condition={isExpanded && !!data.examProperties?.length}>
				<div className="exam-card__properties">
					{(data.examProperties ?? []).map((property, index) => (
						<div key={index} className="exam-card__property-row">
							<span className="exam-card__property-name">{property.displayedName}</span>
							<div className="exam-card__property-stats">
								<span className="exam-card__stat exam-card__stat--correct">
									<i className="icons-check-circle" />
									<span className="exam-card__stat-value">{property.totalTrueAnswer}</span>
								</span>
								<span className="exam-card__stat exam-card__stat--wrong">
									<i className="icons-close-circle" />
									<span className="exam-card__stat-value">{property.totalFalseAnswer}</span>
								</span>
								<span className="exam-card__stat exam-card__stat--empty">
									<i className="icons-minus-circle" />
									<span className="exam-card__stat-value">{(property.totalEmptyAnswer || 0) + (property.totalUnseenAnswer || 0)}</span>
								</span>
							</div>
						</div>
					))}
				</div>
			</If>

			<TopicAnalysisModal
				isOpen={isTopicAnalysisOpen}
				onClose={() => setIsTopicAnalysisOpen(false)}
				studentId={studentId}
				examId={data.examId}
			/>
		</div>
	);
};

export default ExamCard;
